import React, { useState, useEffect } from 'react';
import type { RevealStepsScreen as RevealStepsScreenType } from '../../types/course';
import { playTickSound, playCorrectSound, playIncorrectSound } from '../../utils/sound';

interface Props {
  screen: RevealStepsScreenType;
  onInteractionComplete: () => void;
  completed: boolean;
}

export const RevealStepsScreen: React.FC<Props> = ({
  screen,
  onInteractionComplete,
  completed,
}) => {
  const [revealedCount, setRevealedCount] = useState(completed ? screen.steps.length : 0);
  const [quickCheckAnswer, setQuickCheckAnswer] = useState<number | null>(null);
  const [quickCheckWrong, setQuickCheckWrong] = useState(false);

  const allRevealed = revealedCount === screen.steps.length;
  const nextStep = revealedCount < screen.steps.length ? screen.steps[revealedCount] : null;
  const pendingQuickCheck = nextStep?.quickCheck && quickCheckAnswer === null;

  useEffect(() => {
    if (allRevealed && !completed) {
      onInteractionComplete();
      playCorrectSound();
    }
  }, [allRevealed, completed, onInteractionComplete]);

  const handleRevealNext = () => {
    if (allRevealed) return;
    // If there's a quick-check pending, don't reveal yet
    if (pendingQuickCheck) return;
    playTickSound();
    setRevealedCount((prev) => prev + 1);
    setQuickCheckAnswer(null);
    setQuickCheckWrong(false);
  };

  const handleQuickCheckAnswer = (index: number) => {
    if (!nextStep?.quickCheck || quickCheckAnswer !== null) return;
    const correct = index === nextStep.quickCheck.correctIndex;
    if (correct) {
      setQuickCheckAnswer(index);
      playTickSound();
      // Auto-reveal after a short delay
      setTimeout(() => {
        setRevealedCount((prev) => prev + 1);
        setQuickCheckAnswer(null);
        setQuickCheckWrong(false);
      }, 500);
    } else {
      playIncorrectSound();
      setQuickCheckWrong(true);
      setTimeout(() => setQuickCheckWrong(false), 600);
    }
  };

  return (
    <div className="flex flex-col gap-4 px-1">
      {screen.icon && <div className="text-3xl text-center">{screen.icon}</div>}
      <h3 className="text-lg font-semibold font-serif text-[var(--clue-text-primary)] text-center">
        {screen.title}
      </h3>

      {screen.body && (
        <>
          <p className="text-sm leading-relaxed text-[var(--clue-text-secondary)]">
            {screen.body}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-px bg-[var(--clue-border-mid)]" />
            <span className="text-[10px] font-semibold text-[var(--clue-text-tertiary)] uppercase tracking-widest">Your turn</span>
            <div className="flex-1 h-px bg-[var(--clue-border-mid)]" />
          </div>
        </>
      )}

      {screen.context && (
        <p className="text-sm text-[var(--clue-text-secondary)] leading-relaxed">{screen.context}</p>
      )}

      <div className="space-y-2">
        {screen.steps.map((step, i) => {
          const isRevealed = i < revealedCount;
          const isNext = i === revealedCount;
          return (
            <div key={i}>
              <div
                className={`rounded-lg border p-3 transition-all duration-300 ${
                  isRevealed
                    ? 'bg-[var(--clue-green-accent)]/5 border-[var(--clue-green-accent)]/30'
                    : isNext
                      ? 'bg-[var(--clue-bg-secondary)] border-[var(--clue-green-accent)]/50'
                      : 'bg-[var(--clue-bg-secondary)] border-[var(--clue-border-mid)] opacity-50'
                }`}
              >
                <div className="flex gap-2 items-start">
                  <span
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 ${
                      isRevealed
                        ? 'bg-[var(--clue-green-accent)] text-white'
                        : 'bg-[var(--clue-border-mid)] text-[var(--clue-text-tertiary)]'
                    }`}
                  >
                    {isRevealed ? '✓' : i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${
                      isRevealed ? 'text-[var(--clue-text-primary)]' : 'text-[var(--clue-text-secondary)]'
                    }`}>
                      {step.label}
                    </p>
                    {isRevealed && (
                      <p className="text-xs text-[var(--clue-text-secondary)] mt-1 leading-relaxed">
                        {step.detail}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick check for the next step */}
              {isNext && pendingQuickCheck && nextStep?.quickCheck && (
                <div className={`mt-2 rounded-lg border p-3 bg-[var(--clue-bg-secondary)] border-[var(--clue-border-mid)] ${
                  quickCheckWrong ? 'animate-shake' : ''
                }`}>
                  <p className="text-xs font-medium text-[var(--clue-text-primary)] mb-2">
                    {nextStep.quickCheck.question}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {nextStep.quickCheck.options.map((opt, oi) => (
                      <button
                        key={oi}
                        onClick={() => handleQuickCheckAnswer(oi)}
                        className="px-3 py-1 rounded-full border border-[var(--clue-border-mid)] text-xs font-medium text-[var(--clue-text-primary)] hover:border-[var(--clue-green-accent)] hover:bg-[var(--clue-green-accent)]/10 transition-colors"
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Tap to reveal next button */}
      {!allRevealed && !pendingQuickCheck && (
        <button
          onClick={handleRevealNext}
          className="w-full py-2.5 rounded-lg border border-dashed border-[var(--clue-green-accent)]/50 text-sm font-medium text-[var(--clue-green-accent)] hover:bg-[var(--clue-green-accent)]/10 transition-colors"
        >
          Tap to reveal step {revealedCount + 1}
        </button>
      )}
    </div>
  );
};
